/**
 * ReportsNative formatters — Intl.NumberFormat-backed helpers shared by
 * every component in the kit. All formatters render `—` (EM_DASH) for
 * null / undefined / NaN, never "Infinity" or "NaN".
 *
 * Percent inputs are PERCENT UNITS (69.3 → "69.3%"), matching the
 * /reportData/* payloads, not 0–1 ratios.
 */

export const EM_DASH = '—'; // —
export const MINUS = '−'; // − (true minus, per mocks)

const nfCache = new Map();

function nf(options) {
  const key = JSON.stringify(options);
  let inst = nfCache.get(key);
  if (!inst) {
    inst = new Intl.NumberFormat('en-US', options);
    nfCache.set(key, inst);
  }
  return inst;
}

export function isBlank(value) {
  return value === null || value === undefined || value === '' || (typeof value === 'number' && !Number.isFinite(value));
}

function toNumber(value) {
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : null;
}

/** Replace the hyphen-minus Intl emits with the typographic minus used in the mocks. */
function fixMinus(str) {
  return str.replace(/-/g, MINUS);
}

export function formatNumber(value, { decimals = 0, maxDecimals, compact = false } = {}) {
  const n = toNumber(value);
  if (n === null) return EM_DASH;
  const opts = compact
    ? { notation: 'compact', maximumFractionDigits: maxDecimals ?? 1 }
    : { minimumFractionDigits: decimals, maximumFractionDigits: maxDecimals ?? decimals };
  return fixMinus(nf(opts).format(n));
}

export function formatCurrency(value, { currency = 'USD', decimals = 0, compact = false } = {}) {
  const n = toNumber(value);
  if (n === null) return EM_DASH;
  const opts = compact
    ? { style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1 }
    : { style: 'currency', currency, minimumFractionDigits: decimals, maximumFractionDigits: decimals };
  return fixMinus(nf(opts).format(n));
}

export function formatPercent(value, { decimals = 1 } = {}) {
  const n = toNumber(value);
  if (n === null) return EM_DASH;
  return `${fixMinus(nf({ minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n))}%`;
}

/**
 * Prefix a formatted value with an explicit sign: "+1,234" / "−1,234".
 * Zero gets no sign.
 */
export function withSign(formatted, rawValue) {
  if (formatted === EM_DASH) return formatted;
  const n = toNumber(rawValue);
  if (n === null) return formatted;
  if (n > 0) return `+${formatted}`;
  // negative values already carry MINUS from fixMinus
  return formatted;
}

/* ------------------------------------------------------------------ */
/* Named format registry — column configs reference these by key.      */
/* ------------------------------------------------------------------ */

const FORMATS = {
  text: (v) => (isBlank(v) ? EM_DASH : String(v)),
  number: (v) => formatNumber(v, { decimals: 0 }),
  number1: (v) => formatNumber(v, { decimals: 1 }),
  number2: (v) => formatNumber(v, { decimals: 2 }),
  numberCompact: (v) => formatNumber(v, { compact: true }),
  hours: (v) => formatNumber(v, { decimals: 2 }),
  currency: (v, o) => formatCurrency(v, { currency: o?.currency, decimals: 0 }),
  currency2: (v, o) => formatCurrency(v, { currency: o?.currency, decimals: 2 }),
  currencyCompact: (v, o) => formatCurrency(v, { currency: o?.currency, compact: true }),
  percent: (v) => formatPercent(v, { decimals: 0 }),
  percent1: (v) => formatPercent(v, { decimals: 1 }),
  percent2: (v) => formatPercent(v, { decimals: 2 }),
};

/**
 * Resolve a format spec — either a key from the registry above or a
 * custom `(value) => string` function — into a formatter function.
 */
export function getFormatter(format, options) {
  if (typeof format === 'function') return format;
  const fn = FORMATS[format] || FORMATS.number;
  return (v) => fn(v, options);
}

/** One-shot convenience: formatValue(1234, 'currency') → "$1,234". */
export function formatValue(value, format = 'number', options) {
  return getFormatter(format, options)(value);
}

/** Signed variant used by variance cells: formatSigned(151, 'currency') → "+$151". */
export function formatSigned(value, format = 'number', options) {
  return withSign(formatValue(value, format, options), value);
}

/* ------------------------------------------------------------------ */
/* Freshness label                                                     */
/* ------------------------------------------------------------------ */

/**
 * "today 9:31 AM" / "yesterday 4:12 PM" / "Jun 10, 4:12 PM" from a Date
 * or ISO string — used by the filter-bar freshness indicator.
 */
export function formatRefreshedAt(refreshedAt) {
  if (!refreshedAt) return null;
  const d = refreshedAt instanceof Date ? refreshedAt : new Date(refreshedAt);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, now)) return `today ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(d, yesterday)) return `yesterday ${time}`;
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${date}, ${time}`;
}
